/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import TemplateText from 'src/components/TemplateText/TemplateText';
import { BhiDateTimePicker } from 'src/components/Inputs/BhiDateTimePicker';
import { SectionProps } from '../../../registry/sectionRegistry';
import { TextToWinTemplateSettings, TemplateFieldControl } from '../../../types';
import {
  normalizeTextToWinTemplateSettings,
  validateTextToWinTemplateSettings,
  REQUIRED_TEXT_TO_WIN_TEMPLATE_FIELDS,
} from '../../../helpers';
import { SettingsControl } from '../SettingsControl';
import { toDayjs, toIso } from '../dateValue';
import { noTopGutterSx, Panel } from 'src/UIKit';

type SettingsKey = keyof Omit<TextToWinTemplateSettings, 'visible'>;

const DATE_FIELDS: { key: SettingsKey; label: string }[] = [
  { key: 'dateStart', label: 'Start Date' },
  { key: 'dateEnd', label: 'End Date' },
  { key: 'dateDraw', label: 'Draw Date' },
  { key: 'dateResultsSend', label: 'Results Send Date' },
];

const NUMBER_FIELDS: { key: SettingsKey; label: string }[] = [
  { key: 'entryIntervalMinutes', label: 'Entry Interval (minutes)' },
  { key: 'maxEntriesPerInterval', label: 'Max Entries Per Interval' },
  { key: 'maxEntriesPerContact', label: 'Max Entries Per Contact' },
  { key: 'winnersCount', label: 'Winners Count' },
];

const TEXT_FIELDS: { key: SettingsKey; label: string }[] = [
  { key: 'entrySuccessMessageText', label: 'Entry Success Message' },
  { key: 'winnerMessageText', label: 'Winner Message' },
  { key: 'entryIntervalLimitMessageText', label: 'Entry Interval Limit Message' },
  { key: 'entryTotalLimitMessageText', label: 'Entry Total Limit Message' },
  { key: 'entryNotStartedMessageText', label: 'Entry Not Started Message' },
  { key: 'entryEndedMessageText', label: 'Entry Ended Message' },
  { key: 'nonWinnerMessageText', label: 'Non-Winner Message' },
];

/**
 * Template settings for text-to-win campaigns. Lets the admin set the default
 * value + visible/required for each runtime field (dates, entry limits, winner
 * counts, and the various automated message texts).
 */
export const TextToWinSettingsSection: React.FC<SectionProps> = ({
  config,
  onConfigUpdate,
  customFields = [],
}) => {
  const settings = normalizeTextToWinTemplateSettings(config.textToWinSettings);
  const errors = validateTextToWinTemplateSettings(settings);

  const updateControl = (key: SettingsKey, next: TemplateFieldControl<any>) => {
    onConfigUpdate({ textToWinSettings: { ...settings, [key]: next } });
  };

  const isLocked = (key: SettingsKey) => REQUIRED_TEXT_TO_WIN_TEMPLATE_FIELDS.includes(key);

  return (
    <Grid item xs={12} sx={noTopGutterSx}>
      <Panel>
        <Typography variant="subtitle1" mb={2} fontWeight="fontWeightBold">
          Text to Win Settings
        </Typography>

        {DATE_FIELDS.map(({ key, label }) => (
          <SettingsControl
            key={key}
            label={label}
            control={settings[key] as TemplateFieldControl<string>}
            locked={isLocked(key)}
            onChange={next => updateControl(key, next)}
            error={errors[`textToWinSettings.${key}`]}
          >
            {(value, setValue) => (
              <BhiDateTimePicker allowEmpty value={toDayjs(value)} onChange={next => setValue(toIso(next))} />
            )}
          </SettingsControl>
        ))}

        {NUMBER_FIELDS.map(({ key, label }) => (
          <SettingsControl
            key={key}
            label={label}
            control={settings[key] as TemplateFieldControl<number | null>}
            locked={isLocked(key)}
            onChange={next => updateControl(key, next)}
          >
            {(value, setValue) => (
              <TextField
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => setValue(e.target.value === '' ? null : Number(e.target.value))}
                inputProps={{ 'aria-label': `${label} default`, min: 1 }}
              />
            )}
          </SettingsControl>
        ))}

        {TEXT_FIELDS.map(({ key, label }) => (
          <SettingsControl
            key={key}
            label={label}
            control={settings[key] as TemplateFieldControl<string | null>}
            locked={isLocked(key)}
            onChange={next => updateControl(key, next)}
          >
            {(value, setValue) => (
              <TemplateText
                showConfirmationText={false}
                confirmationText=""
                setFocus={false}
                customFields={customFields}
                messageTemplates={undefined}
                text={value || ''}
                updateText={newText => setValue(newText)}
              />
            )}
          </SettingsControl>
        ))}
      </Panel>
    </Grid>
  );
};
